import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import RoadmapCard from "@/components/RoadmapCard";
import VerificationLabel from "@/components/VerificationLabel";
import { api } from "@/lib/api";
import { ArrowRight, AlertTriangle, ExternalLink } from "lucide-react";

export function RoadmapIndex() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/roadmaps", { params: { page_size: 100 } })
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <SEO
        title="Florida Transfer Roadmaps | Beyond Valencia"
        description="Semester-by-semester transfer pathways from Florida community colleges to state universities."
        canonical="/roadmaps"
      />
      <section className="bg-bone border-b border-stone/30">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
          <div className="text-[11px] uppercase tracking-[0.24em] text-brass font-medium">Semester-by-Semester</div>
          <h1 className="mt-5 font-serif font-medium text-4xl sm:text-5xl lg:text-6xl text-ivy tracking-tight leading-[1.05]">
            Transfer Roadmaps
          </h1>
          <p className="mt-6 max-w-3xl text-base sm:text-lg text-ink-soft leading-relaxed">
            Curated four-semester pathways from Florida community colleges to state and selective universities. Each roadmap is verified against the Common Prerequisites Manual and the receiving university&rsquo;s transfer office.
          </p>
        </div>
      </section>
      <section className="bg-bone-dim/30">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 lg:py-16">
          {loading ? <p className="text-sm text-stone">Loading…</p> : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="roadmaps-index-grid">
              {items.map((r) => (
                <Link key={r.slug} to={`/roadmaps/${r.slug}`} className="block" data-testid={`roadmap-card-link-${r.slug}`}>
                  <RoadmapCard
                    title={r.title || r.slug}
                    subtitle={r.from_institution && r.to_institution ? `${r.from_institution.name} → ${r.to_institution.name}` : "Pathway"}
                    stages={r.stages || []}
                    status={r.verification_status || "awaiting"}
                  />
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}

export function RoadmapDetail() {
  const { slug } = useParams();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  useEffect(() => {
    api.get(`/roadmaps/${slug}`).then((res) => setR(res.data)).catch(() => setError(true)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <Layout><div className="max-w-4xl mx-auto p-16 text-stone">Loading…</div></Layout>;
  if (error || !r) return <Layout><div className="max-w-4xl mx-auto p-16"><h1 className="font-serif text-4xl text-ivy">Not found</h1></div></Layout>;

  const isPlaceholder = r._placeholder || r.verification_status !== "verified";
  const fromName = r.from_institution?.name || "Community College";
  const toName = r.to_institution?.name || "University";
  const majorName = r.major?.name;

  return (
    <Layout>
      <SEO
        title={`${fromName} to ${toName}${majorName ? ` — ${majorName}` : ""} Transfer Roadmap | Beyond Valencia`}
        description={isPlaceholder ? `${r.title} — roadmap coming soon.` : (r.body_markdown || `Semester-by-semester pathway from ${fromName} to ${toName}.`).slice(0, 200)}
        canonical={`/roadmaps/${slug}`}
        noindex={isPlaceholder}
        jsonLd={!isPlaceholder && r.stages?.length ? {
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": r.title,
          "step": r.stages.map((s, i) => ({ "@type": "HowToStep", "position": i + 1, "name": s.semester_label, "itemListElement": (s.milestones || []).map((m) => ({ "@type": "HowToDirection", "text": m })) })),
        } : null}
      />

      <section className="bg-ivy text-bone border-b border-ivy-light" data-testid="roadmap-detail-hero">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
          <div className="flex items-baseline gap-3 mb-4">
            <span className="text-[10px] uppercase tracking-[0.24em] text-brass-soft font-medium">Roadmap</span>
            <VerificationLabel status={r.verification_status || "awaiting"} />
          </div>
          <h1 className="font-serif font-medium text-4xl sm:text-5xl lg:text-[3.25rem] tracking-tight leading-[1.05]">
            {r.title}
          </h1>
          {r.from_institution && r.to_institution && (
            <p className="mt-6 text-base text-bone-dim">
              From <Link to={`/community-colleges/${r.from_institution.slug}`} className="text-brass border-b border-brass/50">{r.from_institution.name}</Link>
              {" → "}
              <Link to={`/universities/${r.to_institution.slug}`} className="text-brass border-b border-brass/50">{r.to_institution.name}</Link>
              {majorName && <span> · {majorName}</span>}
            </p>
          )}
        </div>
      </section>

      <section className="bg-bone">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
          {isPlaceholder ? (
            <div className="border border-brass/40 bg-brass/5 p-8 lg:p-10" data-testid="roadmap-placeholder">
              <div className="text-[11px] uppercase tracking-[0.24em] text-brass font-semibold">Roadmap Coming Soon</div>
              <h2 className="mt-4 font-serif font-medium text-3xl text-ivy tracking-tight leading-tight">
                This roadmap is being verified.
              </h2>
              <p className="mt-5 text-base text-ink-soft leading-relaxed">
                Ask NEO to draft a personalized version, or book a Transfer Audit to have our team build it for you.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link to="/neo" className="inline-flex items-center gap-2 bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-5 transition-colors">
                  Ask NEO to build this <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <Link to="/transfer-audit" className="inline-flex items-center gap-2 border border-ivy text-ivy hover:bg-ivy hover:text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-5 transition-colors">
                  Book a Transfer Audit
                </Link>
              </div>
            </div>
          ) : (
            <>
              <RoadmapCard title={r.title} subtitle={r.from_institution && r.to_institution ? `${r.from_institution.name} → ${r.to_institution.name}` : ""} stages={r.stages || []} status={r.verification_status || "awaiting"} />

              {r.common_mistakes?.length > 0 && (
                <div className="mt-12 border-l-4 border-crimson bg-crimson/5 px-6 py-5">
                  <div className="flex items-baseline gap-2 mb-3">
                    <AlertTriangle className="h-4 w-4 text-crimson flex-shrink-0" />
                    <div className="text-[11px] uppercase tracking-[0.24em] font-semibold text-crimson">Common mistakes</div>
                  </div>
                  <ul className="space-y-2 text-sm text-ink-soft leading-relaxed">
                    {r.common_mistakes.map((m, i) => <li key={i}>• {m}</li>)}
                  </ul>
                </div>
              )}

              {r.official_sources?.length > 0 && (
                <div className="mt-12">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-brass font-semibold mb-3">Official sources</div>
                  <ul className="space-y-2">
                    {r.official_sources.map((s, i) => (
                      <li key={i} className="text-sm">
                        <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-brass border-b border-brass/40 hover:text-brass-soft">
                          <ExternalLink className="h-3 w-3 inline mr-1" />{s.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
